import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators, UntypedFormGroup, AbstractControl } from '@angular/forms';

import { of, tap, switchMap, finalize, catchError } from 'rxjs';

import { FORBIDDEN_NAMES } from '@shared/restaurant/constants';
import {
  ValidationService,
  forbiddenNamesValidator,
  NAME_PATTERN,
  USERNAME_PATTERN,
} from '@tablesready/common-ui';
import { PublicInviteApiService } from '../services';
import { CurrentInviteService } from './current-invite.service';
import { LoginService } from '../auth/services';
import { AuthService } from '@tablesready/frontend-auth';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-set-password',
  templateUrl: './signup-by-invite.component.html',
  styleUrls: ['./signup-by-invite.component.scss'],
})
export class SignupByInviteComponent implements OnInit, OnDestroy {
  form: FormGroup;
  inProgress = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly alertsService: AlertsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly loginService: LoginService,
    private readonly validationService: ValidationService,
    private readonly currentInviteService: CurrentInviteService,
    private readonly publicInviteApiService: PublicInviteApiService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        name: [
          this.currentInviteService.value.name,
          [
            Validators.required,
            Validators.maxLength(50),
            Validators.pattern(NAME_PATTERN),
            forbiddenNamesValidator(FORBIDDEN_NAMES),
          ],
        ],
        username: [
          '',
          [
            Validators.required,
            Validators.maxLength(50),
            Validators.pattern(USERNAME_PATTERN),
          ],
          [
            (control: AbstractControl) => this.validationService.usernameUsedValidator(control.value),
          ],
        ],
        password: ['', [Validators.required, this.validationService.passwordValidator()]],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: this.password.bind(this),
      });
  }

  ngOnDestroy(): void {
    this.currentInviteService.clear();
  }

  signup(): void {
    this.inProgress = true;
    const { name, username, password } = this.form.value;

    this.authService.signupByInvite(this.currentInviteService.value.id, {name, username, password})
      .pipe(
        switchMap(() => this.loginService.login({username, password})),
        tap(() => this.router.navigateByUrl('/settings/account/profile?accountsAccessTab=true')),
        catchError(() => {
          this.alertsService.addError('an error occurred during signup');

          return of(null);
        }),
        finalize(() => this.inProgress = false),
      )
      .subscribe();
  }

  cancel(): void {
    this.inProgress = true;
    const { token } = this.activatedRoute.snapshot.queryParams;

    this.publicInviteApiService.decline(token)
      .pipe(
        tap(() => this.router.navigateByUrl('/login')),
        catchError(() => {
          this.alertsService.addError('an error occurred during canceling invite.');

          return of(null);
        }),
        finalize(() => this.inProgress = false),
      ).subscribe();
  }

  get nameControl(): AbstractControl {
    return this.form.get('name');
  }

  get usernameControl(): AbstractControl {
    return this.form.get('username');
  }

  get passwordControl(): AbstractControl {
    return this.form.get('password');
  }

  get confirmPasswordControl(): AbstractControl {
    return this.form.get('confirmPassword');
  }

  private password(formGroup: UntypedFormGroup): { passwordNotMatch: boolean } | null {
    const { value: password } = formGroup.get('password');
    const { value: confirmPassword } = formGroup.get('confirmPassword');
    return password === confirmPassword ? null : { passwordNotMatch: true };
  }
}
